import React from 'react';
import { AlertCircle, Info, Package, Plus, Minus, Upload } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Label } from '../../components/ui/label';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Checkbox } from '../../components/ui/checkbox';

const PackagesStep = (ctx) => {
  const {
    rushDropoff, setRushDropoff,
    numberOfPackages, setNumberOfPackages,
    hasOversizedPackage, setHasOversizedPackage,
    packageDetails, setPackageDetails,
    uploadedFiles,
    liabilityWaiverAccepted, setLiabilityWaiverAccepted,
    packagingAddons, setPackagingAddons,
    errors,
    isSubscription,
    subscriptionUsage,
    carriers,
    packageSizes,
    handlePackageDetailChange,
    handleFileUpload,
    handlePackagingChange,
    handleBackStep,
    handleNextFromPackages,
  } = ctx;

  return (
    <>
            {/* Drop-Off Policy Section */}
            <div style={{ 
              marginTop: '1.5rem',
              marginBottom: '1.5rem',
              padding: '1.25rem',
              backgroundColor: '#f0f9ff',
              borderRadius: '8px',
              border: '2px solid #0ea5e9',
            }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem' }}>
                <Info size={24} style={{ color: '#0ea5e9', flexShrink: 0, marginTop: '2px' }} />
                <div>
                  <h3 style={{ 
                    fontSize: '1.1rem', 
                    fontWeight: '600', 
                    color: '#0c4a6e',
                    marginBottom: '0.5rem' 
                  }}>
                    📦 Drop-Off Policy
                  </h3>
                  <p style={{ fontSize: '0.95rem', color: '#075985', lineHeight: '1.6', margin: 0 }}>
                    <strong>Standard Service:</strong> Your packages will be dropped off at the carrier within <strong>48 calendar hours</strong> of pickup.
                    This is our standard service window included in your base price.
                  </p>
                  <p style={{ fontSize: '0.9rem', color: '#0c4a6e', marginTop: '0.75rem', fontStyle: 'italic' }}>
                    Need faster service? Select "Rush Drop-Off" below for next-business-day delivery to carrier.
                  </p>
                </div>
              </div>
            </div>

            {/* Number of Packages - COUNTER */}
            <div className="form-group">
              <Label htmlFor="packages">How many packages are you returning?</Label>
              <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginTop: '0.5rem' }}>
                <Button
                  type="button"
                  onClick={() => setNumberOfPackages(Math.max(1, numberOfPackages - 1))}
                  style={{
                    width: '48px',
                    height: '48px',
                    padding: 0,
                    backgroundColor: '#dc2626',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '24px',
                    fontWeight: 'bold',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <Minus size={20} />
                </Button>
                
                <div style={{
                  fontSize: '2rem',
                  fontWeight: 'bold',
                  color: '#111827',
                  minWidth: '80px',
                  textAlign: 'center',
                  padding: '12px 24px',
                  backgroundColor: '#f9fafb',
                  borderRadius: '8px',
                  border: '2px solid #e5e7eb'
                }}>
                  {numberOfPackages}
                </div>
                
                <Button
                  type="button"
                  onClick={() => setNumberOfPackages(numberOfPackages + 1)}
                  disabled={numberOfPackages >= 10}
                  style={{
                    width: '48px',
                    height: '48px',
                    padding: 0,
                    backgroundColor: numberOfPackages >= 10 ? '#9ca3af' : '#10b981',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '24px',
                    fontWeight: 'bold',
                    cursor: numberOfPackages >= 10 ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <Plus size={20} />
                </Button>
              </div>
              
              <p className="field-note" style={{ marginTop: '0.75rem' }}>
                {!isSubscription ? (
                  <>
                    <strong>Let's Be Pals (Pay-As-You-Go):</strong><br />
                    • <strong>$19.99</strong> — 1 pickup, up to 3 packages included<br />
                    • <strong>$5</strong> per additional package (max 10 total per pickup)<br />
                    • No rollover - one-time use only
                  </>
                ) : (
                  <span style={{ color: '#10b981', fontWeight: '600' }}>
                    ✓ <strong>Subscription Active:</strong><br />
                    • Pickups used: <strong>{subscriptionUsage?.pickups_used_this_month || 0}/{
                      subscriptionUsage?.plan_type === 'best-pals' ? 4 : 
                      subscriptionUsage?.plan_type === 'my-pal' ? 3 : 2
                    }</strong><br />
                    • Packages used this month: <strong>{subscriptionUsage?.packages_used_this_month || 0}/{
                      subscriptionUsage?.plan_type === 'best-pals' ? 20 : 
                      subscriptionUsage?.plan_type === 'my-pal' ? 15 : 10
                    }</strong><br />
                    • Packages <strong>roll over</strong> within the month (max 10 per pickup)<br />
                    • Additional packages: <strong>$5 each</strong> | Additional pickups: <strong>$15 each</strong>
                  </span>
                )}
              </p>
              
              {numberOfPackages > 10 && (
                <div style={{ 
                  marginTop: '0.75rem',
                  padding: '12px',
                  backgroundColor: '#fee2e2',
                  borderRadius: '6px',
                  border: '2px solid #dc2626'
                }}>
                  <p style={{ fontSize: '0.95rem', color: '#991b1b', margin: 0, fontWeight: '600' }}>
                    ⚠️ Maximum 10 packages per pickup (all plans)
                  </p>
                  <p style={{ fontSize: '0.9rem', color: '#7f1d1d', marginTop: '0.5rem', marginBottom: 0 }}>
                    Please reduce to 10 packages or sign up for subscription to handle more.
                  </p>
                </div>
              )}
              
              {isSubscription && subscriptionUsage && subscriptionUsage.packages_used_this_month + numberOfPackages > 20 && (
                <div style={{ 
                  marginTop: '0.75rem',
                  padding: '12px',
                  backgroundColor: '#fef3c7',
                  borderRadius: '6px',
                  border: '2px solid #f59e0b'
                }}>
                  <p style={{ fontSize: '0.95rem', color: '#78350f', margin: 0, fontWeight: '600' }}>
                    ⚠️ Exceeding Monthly Package Limit
                  </p>
                  <p style={{ fontSize: '0.9rem', color: '#92400e', marginTop: '0.5rem', marginBottom: 0 }}>
                    You've used {subscriptionUsage.packages_used_this_month} packages this month. This booking would total {subscriptionUsage.packages_used_this_month + numberOfPackages} packages (limit: 20/month).
                    <br /><strong>Additional pickup fee: $25</strong>
                  </p>
                </div>
              )}
              
              {errors.packages && <span className="error-message">{errors.packages}</span>}
            </div>

            {/* Oversized Package Option */}
            <div className="form-group">
              <div style={{ 
                padding: '1rem', 
                backgroundColor: '#fee2e2', 
                borderRadius: '8px',
                border: '2px solid #dc2626'
              }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem' }}>
                  <Checkbox
                    id="oversizedPackage"
                    checked={hasOversizedPackage}
                    onCheckedChange={setHasOversizedPackage}
                  />
                  <Label htmlFor="oversizedPackage" style={{ marginBottom: 0, cursor: 'pointer', flex: 1 }}>
                    <div>
                      <span style={{ fontSize: '1rem', fontWeight: '600', color: '#991b1b' }}>
                        📏 Oversized Package (+$8 Handling Fee)
                      </span>
                    </div>
                    <p style={{ fontSize: '0.9rem', color: '#7f1d1d', marginTop: '0.5rem', marginBottom: 0 }}>
                      Check this if you have a package larger than a Large box (24×18×16 inches)
                    </p>
                  </Label>
                </div>
                
                {hasOversizedPackage && (
                  <div style={{ 
                    marginTop: '0.75rem',
                    marginLeft: '2rem',
                    padding: '12px',
                    backgroundColor: 'white',
                    borderRadius: '6px',
                    fontSize: '0.85rem',
                    color: '#7f1d1d'
                  }}>
                    <strong>⚠️ Important:</strong> ReturnPal does not sell packaging larger than Large size. 
                    Oversized items must be boxed by the customer. Maximum size accepted: 36×24×24 inches.
                  </div>
                )}
              </div>
            </div>

            {/* Package Details */}
            <div className="form-group">
              <Label>Package Details</Label>
              <p className="field-note">For each package, upload label/QR and provide details</p>
              
              {/* IMPORTANT OPERATIONAL NOTICE */}
              <div style={{ 
                marginTop: '1rem',
                marginBottom: '1.5rem',
                padding: '1.25rem',
                backgroundColor: '#fef2f2',
                borderRadius: '8px',
                border: '2px solid #dc2626',
              }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem' }}>
                  <AlertCircle size={24} style={{ color: '#dc2626', flexShrink: 0, marginTop: '2px' }} />
                  <div>
                    <h3 style={{ 
                      fontSize: '1rem', 
                      fontWeight: '700', 
                      color: '#991b1b',
                      marginBottom: '0.5rem' 
                    }}>
                      ⚠️ IMPORTANT: Package Collection Policy
                    </h3>
                    <p style={{ fontSize: '0.9rem', color: '#7f1d1d', lineHeight: '1.6', margin: 0 }}>
                      <strong>Operators will only collect the number of return packages selected and paid for during booking.</strong>
                    </p>
                    <p style={{ fontSize: '0.9rem', color: '#7f1d1d', lineHeight: '1.6', marginTop: '0.5rem', marginBottom: 0 }}>
                      Any additional packages presented at pickup must be added and paid for before they can be accepted, or they will be left behind.
                    </p>
                    <p style={{ fontSize: '0.85rem', color: '#991b1b', marginTop: '0.75rem', fontStyle: 'italic', marginBottom: 0 }}>
                      If the total number of packages exceeds the allowed limit for the booked pickup, an additional pickup fee will apply.
                    </p>
                  </div>
                </div>
              </div>
              
              {/* Booking-wide Return Method Acknowledgment & Liability Waiver */}
              <div
                data-testid="liability-waiver-block"
                style={{
                  marginBottom: '1.5rem',
                  padding: '1.25rem',
                  backgroundColor: liabilityWaiverAccepted ? '#ecfdf5' : '#fffbeb',
                  border: `2px solid ${liabilityWaiverAccepted ? '#10b981' : '#f59e0b'}`,
                  borderRadius: '8px',
                }}
              >
                <label style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={liabilityWaiverAccepted}
                    onChange={(e) => setLiabilityWaiverAccepted(e.target.checked)}
                    data-testid="liability-waiver-checkbox"
                    style={{ marginTop: '0.25rem', cursor: 'pointer', width: '20px', height: '20px', flexShrink: 0 }}
                  />
                  <div>
                    <div style={{ fontWeight: 700, fontSize: '1rem', color: liabilityWaiverAccepted ? '#065f46' : '#78350f', marginBottom: '0.5rem' }}>
                      Return Method Acknowledgment & Liability Waiver
                    </div>
                    <p style={{ fontSize: '0.875rem', color: liabilityWaiverAccepted ? '#047857' : '#92400e', lineHeight: 1.6, margin: 0 }}>
                      I acknowledge that Amazon returns must be processed using the correct label or QR code and delivered to the carrier specified in Amazon's return instructions. I understand that I am solely responsible for verifying and selecting the correct return method.
                      <br /><br />
                      I agree that ReturnPal is not responsible for returns delivered to an incorrect carrier or drop‑off location. I understand that errors may result in return refusal, delays, denial of refund, or the need to restart the return process.
                    </p>
                    <p style={{ fontSize: '0.75rem', color: '#dc2626', fontWeight: 600, marginTop: '0.5rem', marginBottom: 0 }}>
                      You must check this box before uploading labels or QR codes.
                    </p>
                  </div>
                </label>
                {errors.liabilityWaiver && (
                  <span className="error-message" style={{ display: 'block', marginTop: '0.5rem' }}>{errors.liabilityWaiver}</span>
                )}
              </div>

              <div className="package-uploads">
                {Array.from({ length: numberOfPackages }, (_, i) => i + 1).map(packageNum => {
                  const pkg = packageDetails[packageNum] || {};
                  const packageType = pkg.packageType || 'label';
                  const uploadGated = !liabilityWaiverAccepted;
                  
                  return (
                    <div key={packageNum} className="package-upload-item" style={{ 
                      padding: '1.5rem', 
                      border: '2px solid #e5e7eb', 
                      borderRadius: '8px', 
                      marginBottom: '1.5rem',
                      backgroundColor: '#f9fafb'
                    }}>
                      <h4 style={{ marginBottom: '1rem', fontWeight: '600', fontSize: '1.1rem', color: '#111827' }}>
                        📦 Package {packageNum}
                      </h4>

                      {/* 1. Select Carrier */}
                      <div style={{ marginBottom: '0.75rem' }}>
                        <Label htmlFor={`carrier${packageNum}`}>
                          Select Carrier <span style={{ color: '#ef4444' }}>*</span>
                        </Label>
                        <Select
                          value={pkg.carrier || ''}
                          onValueChange={(value) => handlePackageDetailChange(packageNum, 'carrier', value)}
                        >
                          <SelectTrigger
                            className={errors[`carrier${packageNum}`] ? 'input-error' : ''}
                            data-testid={`carrier-select-${packageNum}`}
                          >
                            <SelectValue placeholder="Choose carrier" />
                          </SelectTrigger>
                          <SelectContent>
                            {carriers.map(carrier => (
                              <SelectItem key={carrier.id} value={carrier.name}>
                                {carrier.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        {errors[`carrier${packageNum}`] && (
                          <span className="error-message">{errors[`carrier${packageNum}`]}</span>
                        )}
                      </div>
                      <p style={{ fontSize: '0.8rem', color: '#6b7280', marginBottom: '1.25rem', lineHeight: 1.5, fontStyle: 'italic' }}>
                        <strong>Note:</strong> Amazon QR codes are valid only at approved locations shown in Amazon&apos;s return instructions. Customers are responsible for selecting the correct return option and following all Amazon return requirements.
                      </p>

                      {/* 2. Package Type — gated by liability waiver */}
                      <div style={{ marginBottom: '1.25rem', opacity: uploadGated ? 0.5 : 1, pointerEvents: uploadGated ? 'none' : 'auto' }}>
                        <Label style={{ marginBottom: '0.5rem', display: 'block' }}>
                          Package Type <span style={{ color: '#ef4444' }}>*</span>
                          {uploadGated && <span style={{ marginLeft: '0.5rem', fontSize: '0.75rem', color: '#dc2626' }}>(check the waiver above to enable)</span>}
                        </Label>
                        <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
                          <label style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.5rem',
                            padding: '0.75rem 1rem',
                            border: packageType === 'label' ? '2px solid #10b981' : '2px solid #d1d5db',
                            borderRadius: '6px',
                            cursor: uploadGated ? 'not-allowed' : 'pointer',
                            backgroundColor: packageType === 'label' ? '#ecfdf5' : 'white',
                          }}>
                            <input
                              type="radio"
                              name={`packageType${packageNum}`}
                              value="label"
                              checked={packageType === 'label'}
                              disabled={uploadGated}
                              onChange={() => handlePackageDetailChange(packageNum, 'packageType', 'label')}
                              style={{ cursor: 'pointer' }}
                            />
                            <span style={{ fontWeight: '500' }}>📄 I have a shipping label</span>
                          </label>
                          <label style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.5rem',
                            padding: '0.75rem 1rem',
                            border: packageType === 'qr' ? '2px solid #10b981' : '2px solid #d1d5db',
                            borderRadius: '6px',
                            cursor: uploadGated ? 'not-allowed' : 'pointer',
                            backgroundColor: packageType === 'qr' ? '#ecfdf5' : 'white',
                          }}>
                            <input
                              type="radio"
                              name={`packageType${packageNum}`}
                              value="qr"
                              checked={packageType === 'qr'}
                              disabled={uploadGated}
                              onChange={() => handlePackageDetailChange(packageNum, 'packageType', 'qr')}
                              style={{ cursor: 'pointer' }}
                            />
                            <span style={{ fontWeight: '500' }}>📱 I have a QR code</span>
                          </label>
                        </div>
                      </div>

                      {/* 3. Upload Label / QR Code — gated */}
                      <div style={{ marginBottom: '1rem', opacity: uploadGated ? 0.5 : 1, pointerEvents: uploadGated ? 'none' : 'auto' }}>
                        <Label htmlFor={`fileUpload${packageNum}`} className="package-label">
                          {packageType === 'qr' ? 'QR Code Image' : 'Shipping Label'} <span style={{ color: '#ef4444' }}>*</span>
                        </Label>
                        <div className="upload-area">
                          <input
                            type="file"
                            id={`fileUpload${packageNum}`}
                            accept="image/*,.pdf"
                            disabled={uploadGated}
                            onChange={(e) => handleFileUpload(packageNum, e)}
                            className="file-input"
                            data-testid={`file-upload-${packageNum}`}
                          />
                          <label htmlFor={`fileUpload${packageNum}`} className="upload-label">
                            <Upload size={20} />
                            <span>
                              {uploadedFiles[packageNum]
                                ? uploadedFiles[packageNum].name
                                : `Upload ${packageType === 'qr' ? 'QR code' : 'label'}`}
                            </span>
                          </label>
                        </div>
                        {errors[`file${packageNum}`] && (
                          <span className="error-message">{errors[`file${packageNum}`]}</span>
                        )}
                      </div>

                      {/* 4. Tracking last-6 (label) OR critical QR notice (qr) */}
                      {packageType === 'label' ? (
                        <div style={{ marginBottom: '1.25rem' }}>
                          <Label htmlFor={`tracking${packageNum}`}>
                            Last 6 Digits of Tracking Number <span style={{ color: '#ef4444' }}>*</span>
                          </Label>
                          <Input
                            id={`tracking${packageNum}`}
                            type="text"
                            maxLength={6}
                            placeholder="e.g., 456784"
                            value={pkg.tracking_last6 || ''}
                            onChange={(e) => {
                              const value = e.target.value.replace(/\D/g, '');
                              handlePackageDetailChange(packageNum, 'tracking_last6', value);
                            }}
                            className={errors[`tracking${packageNum}`] ? 'input-error' : ''}
                            data-testid={`tracking-${packageNum}`}
                          />
                          <p style={{ fontSize: '0.85rem', color: '#6b7280', marginTop: '0.25rem' }}>
                            You will write these 6 digits on your package for identification
                          </p>
                          {errors[`tracking${packageNum}`] && (
                            <span className="error-message">{errors[`tracking${packageNum}`]}</span>
                          )}
                        </div>
                      ) : (
                        <div style={{
                          padding: '1.25rem',
                          backgroundColor: '#fee2e2',
                          borderRadius: '8px',
                          marginBottom: '1.25rem',
                          border: '3px solid #dc2626',
                        }}>
                          <p style={{ fontSize: '1rem', color: '#991b1b', margin: '0 0 0.5rem 0', fontWeight: '700', textTransform: 'uppercase' }}>
                            ⚠️ CRITICAL PACKAGE IDENTIFICATION
                          </p>
                          <p style={{ fontSize: '0.95rem', color: '#7f1d1d', margin: 0, fontWeight: '600' }}>
                            📱 <strong>QR Code Package:</strong> You will receive a <span style={{ backgroundColor: '#fca5a5', padding: '2px 6px', borderRadius: '4px' }}>ReturnPal ID</span> (e.g., RPQ1042) after booking.<br/>
                            <span style={{ fontSize: '1.05rem', marginTop: '0.5rem', display: 'block' }}>🔴 WRITE THIS ID CLEARLY ON YOUR PACKAGE</span>
                          </p>
                        </div>
                      )}

                      {/* 5. Need Packaging? */}
                      <div style={{ marginBottom: '1rem' }}>
                        <Label htmlFor={`packaging${packageNum}`}>
                          Need Packaging? (Optional)
                        </Label>
                        <Select
                          value={pkg.packaging || 'none'}
                          onValueChange={(value) => handlePackageDetailChange(packageNum, 'packaging', value)}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="No packaging needed" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">No packaging needed</SelectItem>
                            <SelectItem value="small">
                              <div>
                                <strong>Small Box - $3.00</strong>
                                <br />
                                <span style={{ fontSize: '0.85rem', color: '#6b7280' }}>
                                  Up to 12×9×6 inches | Small electronics, accessories, clothing
                                </span>
                              </div>
                            </SelectItem>
                            <SelectItem value="medium">
                              <div>
                                <strong>Medium Box - $4.00</strong>
                                <br />
                                <span style={{ fontSize: '0.85rem', color: '#6b7280' }}>
                                  Up to 18×14×10 inches | Shoe boxes, handbags, small appliances
                                </span>
                              </div>
                            </SelectItem>
                            <SelectItem value="large">
                              <div>
                                <strong>Large Box - $5.00</strong>
                                <br />
                                <span style={{ fontSize: '0.85rem', color: '#6b7280' }}>
                                  Up to 24×18×16 inches | Electronics, multiple clothing items
                                </span>
                              </div>
                            </SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      {/* 6. Item Description */}
                      <div className="package-notes" style={{ marginBottom: '1rem' }}>
                        <Label htmlFor={`notes${packageNum}`}>
                          Item Description <span style={{ color: '#ef4444' }}>*</span>
                        </Label>
                        <textarea
                          id={`notes${packageNum}`}
                          value={pkg.notes || ''}
                          onChange={(e) => handlePackageDetailChange(packageNum, 'notes', e.target.value)}
                          placeholder="Brief description of item being returned (e.g., 'Blue Nike Running Shoes' or 'Samsung Galaxy Phone Case')"
                          className={`notes-textarea ${errors[`notes${packageNum}`] ? 'input-error' : ''}`}
                          rows="2"
                          data-testid={`notes-${packageNum}`}
                        />
                        {errors[`notes${packageNum}`] && (
                          <span className="error-message">{errors[`notes${packageNum}`]}</span>
                        )}
                      </div>

                      {/* 7. Lithium / 8. Fragile checkboxes */}
                      <div style={{ marginBottom: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <Checkbox
                          id={`hasLithiumBatteries${packageNum}`}
                          checked={pkg.hasLithiumBatteries || false}
                          onCheckedChange={(checked) => handlePackageDetailChange(packageNum, 'hasLithiumBatteries', checked)}
                        />
                        <Label htmlFor={`hasLithiumBatteries${packageNum}`} style={{ marginBottom: 0, cursor: 'pointer' }}>
                          Contains Lithium Batteries
                        </Label>
                      </div>
                      <div style={{ marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <Checkbox
                          id={`isFragile${packageNum}`}
                          checked={pkg.isFragile || false}
                          onCheckedChange={(checked) => handlePackageDetailChange(packageNum, 'isFragile', checked)}
                        />
                        <Label htmlFor={`isFragile${packageNum}`} style={{ marginBottom: 0, cursor: 'pointer' }}>
                          Fragile - Extra Care During Handling
                        </Label>
                      </div>

                      {/* 9. Per-Package Rush Drop‑Off */}
                      <div style={{ marginBottom: '0.5rem', padding: '1rem', backgroundColor: '#fef3c7', borderRadius: '8px', border: '2px solid #f59e0b' }}>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', cursor: 'pointer' }}>
                          <input
                            type="checkbox"
                            checked={pkg.rushDelivery || false}
                            onChange={(e) => handlePackageDetailChange(packageNum, 'rushDelivery', e.target.checked)}
                            style={{ cursor: 'pointer', width: '18px', height: '18px' }}
                          />
                          <div>
                            <div style={{ fontWeight: '600', color: '#92400e', fontSize: '0.95rem' }}>
                              ⚡ Rush Delivery for This Package (+$10.00)
                            </div>
                            <div style={{ fontSize: '0.85rem', color: '#78350f', marginTop: '0.25rem' }}>
                              Next business day drop-off to carrier for this package only
                            </div>
                          </div>
                        </label>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>


      {/* Step 2 navigation */}
      <div style={{ marginTop: '1.5rem', display: 'flex', justifyContent: 'space-between', gap: '1rem', flexWrap: 'wrap' }}>
        <Button
          type="button"
          onClick={handleBackStep}
          data-testid="wizard-back-btn-step2"
          variant="outline"
          className="btn-large"
          style={{ minWidth: '140px' }}
        >
          ← Back
        </Button>
        <Button
          type="button"
          onClick={handleNextFromPackages}
          data-testid="wizard-next-btn-step2"
          className="btn-primary btn-large"
          style={{ flex: 1, minWidth: '200px' }}
        >
          Next: Review &amp; Submit →
        </Button>
      </div>
    </>
  );
};

export default PackagesStep;
